import { useState, useEffect, useCallback } from 'react';
import { auth, publicApi, adminApi} from "../lib/api";
import { LogOut, Plus, Pencil, Trash2, Save, X, Leaf, Settings, Image, UtensilsCrossed } from 'lucide-react';


const TABS = [
  { id: 'categories', label: 'Menu Images', icon: Image },
  { id: 'settings', label: 'Site Settings', icon: Settings },
];

const CATEGORY_OPTIONS = [
  { id: 'banh-mi', label: 'Banh Mi' },
  { id: 'rouleaux', label: 'Spring Rolls & Bowls' },
  { id: 'boissons', label: 'Boissons' },
];

const EMPTY_FORM = { name_fr: '', name_en: '', description_fr: '', description_en: '', price: '', price_large: '', category: 'banh-mi', subcategory: '', is_vegetarian: false, display_order: 0 };

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('categories');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [itemsRes, catsRes, settingsRes] = await Promise.all([
        publicApi.getMenu(),
        publicApi.getCategories(),
        publicApi.getSettings(),
      ]);

      setMenuItems(itemsRes.data);
      setCategories(catsRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    try {
      const res = await auth.login({
        email,
        password,
      });

      setUser(res.data);

    } catch (err) {
      setLoginError(
        err.response?.data?.message || "Login failed"
      );
    }
  };

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {}

    setUser(null);
  };

  // Menu CRUD
  const handleSave = async (e) => {
    e.preventDefault();
    const data = { ...form, price: parseFloat(form.price) || 0, price_large: form.price_large ? parseFloat(form.price_large) : null, display_order: parseInt(form.display_order) || 0 };
    try {
      if (editingId) {
        await adminApi.updateMenuItem(editingId, data);
      } else {
        await adminApi.createMenuItem(data);
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); fetchAll();
    } catch (err) {
      alert(err.response.data.message || "Failed");
    }
  };

  const handleEdit = (item) => {
    setForm({ name_fr: item.name_fr, name_en: item.name_en, description_fr: item.description_fr || '', description_en: item.description_en || '', price: item.price, price_large: item.price_large || '', category: item.category, subcategory: item.subcategory || '', is_vegetarian: item.is_vegetarian || false, display_order: item.display_order || 0 });
    setEditingId(item.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await adminApi.deleteMenuItem(id);
      await fetchAll();
    } catch (err) {
      alert(err.response.data.message || "Failed");
    }
  };

  // Category image update
  const handleCategoryUpdate = async (catId, field, value) => {
    try {
      await adminApi.updateCategory(catId, {
        [field]: value,
      });
      await fetchAll();
    } catch (err) { alert('Failed to update'); }
  };

  const handleCategoryImageUpload = async (catId, field, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setSaving(true);
      await adminApi.uploadCategoryImage(catId, field, formData);
      await fetchAll();
    } catch (err) {
      console.error(err);
      alert("Failed to upload image");
    } finally {
      setSaving(false);
    }
  };

  // Settings save
  const handleSettingsSave = async () => {
    setSaving(true);
    try {
      await adminApi.updateSettings(settingsForm);
      await fetchAll();
    } catch (err) { alert('Failed to save settings'); }
    setSaving(false);
  };

  const handleHourChange = (idx, field, value) => {
    const hours = [...(settingsForm.hours || [])];
    hours[idx] = { ...hours[idx], [field]: value };
    setSettingsForm({ ...settingsForm, hours });
  };

  const addHour = () => setSettingsForm({ ...settingsForm, hours: [...(settingsForm.hours || []), { days_fr: '', days_en: '', time: '' }] });
  const removeHour = (idx) => setSettingsForm({ ...settingsForm, hours: (settingsForm.hours || []).filter((_, i) => i !== idx) });
  const displayCategories = categories
    .filter((cat) => cat.id !== 'bols')
    .map((cat) => {
      if (cat.id === 'rouleaux') {
        return {
          ...cat,
          name_fr: 'Rouleaux & Bols',
          name_en: 'Spring Rolls & Bowls',
        };
      }

      return cat;
    });
  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <form onSubmit={handleLogin} data-testid="admin-login-form" className="w-full max-w-sm bg-[#111] border border-[#222] p-8 rounded-sm">
          <h1 className="text-2xl text-[#FFD700] mb-6 tracking-wide" style={{fontFamily:'Bebas Neue'}}>Admin Login</h1>
          {loginError && <p data-testid="login-error" className="text-[#FF6B6B] text-sm mb-4">{loginError}</p>}
          <input data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full bg-black border border-[#333] text-white px-4 py-3 mb-4 text-sm focus:border-[#FFD700] focus:outline-none" />
          <input data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full bg-black border border-[#333] text-white px-4 py-3 mb-6 text-sm focus:border-[#FFD700] focus:outline-none" />
          <button data-testid="login-submit" type="submit" className="w-full bg-[#FFD700] text-black py-3 font-bold text-sm tracking-widest uppercase hover:bg-[#FFEA00] transition-colors">Login</button>
          <a href="/" className="block text-center text-[#B0B0B0] text-sm mt-6 hover:text-[#FFD700]">&larr; Back to site</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="bg-[#111] border-b border-[#222] px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl text-[#FFD700] tracking-wide" style={{fontFamily:'Bebas Neue'}}>Petit Hem Admin</h1>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#B0B0B0]">{user.email}</span>
          <button data-testid="admin-logout" onClick={handleLogout} className="text-[#B0B0B0] hover:text-[#FF6B6B]"><LogOut size={18} /></button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Tab navigation */}
        <div className="flex gap-4 mb-8 border-b border-[#222] pb-4">
          {TABS.map((tab) => (
            <button key={tab.id} data-testid={`admin-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold tracking-wider uppercase transition-colors ${activeTab === tab.id ? 'text-[#FFD700] border-b-2 border-[#FFD700]' : 'text-[#B0B0B0] hover:text-white'}`}
              style={{fontFamily:'Bebas Neue'}}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
          <a href="/" className="ml-auto px-4 py-2 border border-[#333] text-sm text-[#B0B0B0] hover:text-white hover:border-white transition-colors">&larr; Site</a>
        </div>

        {/* MENU ITEMS TAB */}
        {activeTab === 'items' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl tracking-wide" style={{fontFamily:'Bebas Neue'}}>Menu Items ({menuItems.length})</h2>
              <button data-testid="add-item-btn" onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
                className="flex items-center gap-2 bg-[#FFD700] text-black px-4 py-2 font-bold text-sm tracking-wider uppercase hover:bg-[#FFEA00]">
                <Plus size={16} /> Add Item
              </button>
            </div>
            {showForm && (
              <form onSubmit={handleSave} data-testid="menu-item-form" className="bg-[#111] border border-[#222] p-6 mb-8 rounded-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg text-[#FFD700]" style={{fontFamily:'Bebas Neue'}}>{editingId ? 'Edit Item' : 'New Item'}</h3>
                  <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="text-[#B0B0B0] hover:text-white"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input placeholder="Name (FR)" value={form.name_fr} onChange={(e) => setForm({...form, name_fr: e.target.value})} required className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Name (EN)" value={form.name_en} onChange={(e) => setForm({...form, name_en: e.target.value})} required className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Description (FR)" value={form.description_fr} onChange={(e) => setForm({...form, description_fr: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Description (EN)" value={form.description_en} onChange={(e) => setForm({...form, description_en: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} required className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Price Large (optional)" type="number" step="0.01" value={form.price_large} onChange={(e) => setForm({...form, price_large: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none">
                    {CATEGORY_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                  <input placeholder="Subcategory (drinks)" value={form.subcategory} onChange={(e) => setForm({...form, subcategory: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <input placeholder="Display Order" type="number" value={form.display_order} onChange={(e) => setForm({...form, display_order: e.target.value})} className="bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
                  <label className="flex items-center gap-2 text-sm text-[#B0B0B0]">
                    <input type="checkbox" checked={form.is_vegetarian} onChange={(e) => setForm({...form, is_vegetarian: e.target.checked})} className="accent-[#4CAF50]" /> Vegetarian
                  </label>
                </div>
                <button type="submit" data-testid="save-item-btn" className="mt-4 flex items-center gap-2 bg-[#FFD700] text-black px-6 py-2 font-bold text-sm tracking-wider uppercase hover:bg-[#FFEA00]">
                  <Save size={16} /> {editingId ? 'Update' : 'Create'}
                </button>
              </form>
            )}
            <div className="space-y-2">
              {CATEGORY_OPTIONS.map((cat) => {
                const items = menuItems.filter((i) => i.category === cat.id);
                if (items.length === 0) return null;
                return (
                  <div key={cat.id} className="mb-6">
                    <h3 className="text-lg text-[#FFD700] mb-3 tracking-wide" style={{fontFamily:'Bebas Neue'}}>{cat.label} ({items.length})</h3>
                    {items.map((item) => (
                      <div key={item.id} data-testid={`admin-item-${item.id}`} className="flex items-center justify-between py-3 px-4 border-b border-[#222]/50 hover:bg-[#111]">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {item.is_vegetarian && <Leaf size={14} className="text-[#4CAF50]" />}
                            <span className="text-white font-medium">{item.name_fr}</span>
                            <span className="text-[#B0B0B0] text-xs">/ {item.name_en}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[#FFD700] font-bold text-sm">{item.price}$</span>
                          <button onClick={() => handleEdit(item)} className="text-[#B0B0B0] hover:text-[#FFD700]"><Pencil size={14} /></button>
                          <button onClick={() => handleDelete(item.id)} className="text-[#B0B0B0] hover:text-[#FF6B6B]"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* CATEGORIES TAB */}
        {activeTab === 'categories' && (
          <div className="space-y-8">
            <h2 className="text-2xl tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
              Menu Images
            </h2>

            <p className="text-sm text-[#B0B0B0] mb-4">
              Upload menu images for each category. The French image is shown by default.
              The English image is shown when customers switch to English.
            </p>

            {displayCategories.map((cat) => (
              <div key={cat.id} className="bg-[#111] border border-[#222] p-6 rounded-sm">
                <h3 className="text-lg text-[#FFD700] mb-6" style={{ fontFamily: 'Bebas Neue' }}>
                  {cat.name_fr} / {cat.name_en}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-sm text-[#B0B0B0] mb-3 block">
                      French Menu Image
                    </label>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleCategoryImageUpload(cat.id, 'image_url', e.target.files[0])
                      }
                      className="block w-full text-sm text-[#B0B0B0]
                        file:mr-4 file:py-2 file:px-4
                        file:border-0 file:bg-[#FFD700]
                        file:text-black file:font-bold
                        hover:file:bg-[#FFEA00]"
                    />

                    {cat.image_url && (
                      <img
                        src={cat.image_url}
                        alt="French menu"
                        className="mt-4 max-h-64 w-auto border border-[#222] rounded-sm"
                      />
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-[#B0B0B0] mb-3 block">
                      English Menu Image
                    </label>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        handleCategoryImageUpload(cat.id, 'image_url_en', e.target.files[0])
                      }
                      className="block w-full text-sm text-[#B0B0B0]
                        file:mr-4 file:py-2 file:px-4
                        file:border-0 file:bg-[#FFD700]
                        file:text-black file:font-bold
                        hover:file:bg-[#FFEA00]"
                    />

                    {cat.image_url_en && (
                      <img
                        src={cat.image_url_en}
                        alt="English menu"
                        className="mt-4 max-h-64 w-auto border border-[#222] rounded-sm"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && settings && (
          <div className="space-y-6">
            <h2 className="text-2xl tracking-wide" style={{fontFamily:'Bebas Neue'}}>Site Settings</h2>
            <div className="bg-[#111] border border-[#222] p-6 rounded-sm space-y-4">
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Address</label>
                <input data-testid="settings-address" value={settingsForm.address || ''} onChange={(e) => setSettingsForm({...settingsForm, address: e.target.value})}
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Phone</label>
                <input data-testid="settings-phone" value={settingsForm.phone || ''} onChange={(e) => setSettingsForm({...settingsForm, phone: e.target.value})}
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Google Maps Search Query</label>
                <input data-testid="settings-maps" value={settingsForm.maps_query || ''} onChange={(e) => setSettingsForm({...settingsForm, maps_query: e.target.value})}
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none"
                  placeholder="e.g. 900+Rue+Saint-Philippe,+Montreal,+QC" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm text-[#B0B0B0]">Opening Hours</label>
                  <button onClick={addHour} className="text-xs text-[#FFD700] hover:text-[#FFEA00]">+ Add row</button>
                </div>
                {(settingsForm.hours || []).map((h, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                    <input placeholder="Days (FR)" value={h.days_fr || ''} onChange={(e) => handleHourChange(i, 'days_fr', e.target.value)}
                      className="bg-black border border-[#333] text-white px-2 py-1.5 text-sm focus:border-[#FFD700] focus:outline-none" />
                    <input placeholder="Days (EN)" value={h.days_en || ''} onChange={(e) => handleHourChange(i, 'days_en', e.target.value)}
                      className="bg-black border border-[#333] text-white px-2 py-1.5 text-sm focus:border-[#FFD700] focus:outline-none" />
                    <input placeholder="Time" value={h.time || ''} onChange={(e) => handleHourChange(i, 'time', e.target.value)}
                      className="bg-black border border-[#333] text-white px-2 py-1.5 text-sm focus:border-[#FFD700] focus:outline-none" />
                    <button onClick={() => removeHour(i)} className="text-[#FF6B6B] hover:text-red-400 text-sm">Remove</button>
                  </div>
                ))}
              </div>

              <button data-testid="save-settings-btn" onClick={handleSettingsSave} disabled={saving}
                className="flex items-center gap-2 bg-[#FFD700] text-black px-6 py-2 font-bold text-sm tracking-wider uppercase hover:bg-[#FFEA00] disabled:opacity-50">
                <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {/* Delivery URLs */}
            <h3 className="text-xl tracking-wide mt-8" style={{fontFamily:'Bebas Neue'}}>Delivery Platforms</h3>
            <div className="bg-[#111] border border-[#222] p-6 rounded-sm space-y-4">
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Uber Eats URL</label>
                <input data-testid="settings-uber" value={settingsForm.uber_eats_url || ''} onChange={(e) => setSettingsForm({...settingsForm, uber_eats_url: e.target.value})}
                  placeholder="https://www.ubereats.com/store/petit-hem/..."
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">DoorDash URL</label>
                <input data-testid="settings-doordash" value={settingsForm.doordash_url || ''} onChange={(e) => setSettingsForm({...settingsForm, doordash_url: e.target.value})}
                  placeholder="https://www.doordash.com/store/petit-hem/..."
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">SkipTheDishes URL</label>
                <input data-testid="settings-skip" value={settingsForm.skip_the_dishes_url || ''} onChange={(e) => setSettingsForm({...settingsForm, skip_the_dishes_url: e.target.value})}
                  placeholder="https://www.skipthedishes.com/petit-hem/..."
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <button onClick={handleSettingsSave} disabled={saving}
                className="flex items-center gap-2 bg-[#FFD700] text-black px-6 py-2 font-bold text-sm tracking-wider uppercase hover:bg-[#FFEA00] disabled:opacity-50">
                <Save size={16} /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>

            {/* Social / Developer */}
            <h3 className="text-xl tracking-wide mt-8" style={{fontFamily:'Bebas Neue'}}>Social & Credits</h3>
            <div className="bg-[#111] border border-[#222] p-6 rounded-sm space-y-4">
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Instagram URL</label>
                <input data-testid="settings-instagram" value={settingsForm.instagram_url || ''} onChange={(e) => setSettingsForm({...settingsForm, instagram_url: e.target.value})}
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-[#B0B0B0] mb-1 block">Developer LinkedIn URL</label>
                <input data-testid="settings-linkedin" value={settingsForm.developer_linkedin_url || ''} onChange={(e) => setSettingsForm({...settingsForm, developer_linkedin_url: e.target.value})}
                  className="w-full bg-black border border-[#333] text-white px-3 py-2 text-sm focus:border-[#FFD700] focus:outline-none" />
              </div>
              <button onClick={handleSettingsSave} disabled={saving}
                className="flex items-center gap-2 bg-[#FFD700] text-black px-6 py-2 font-bold text-sm tracking-wider uppercase hover:bg-[#FFEA00] disabled:opacity-50">
                <Save size={16} /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
